import { useCompanyListQuery } from "Company/Api/Query/useCompanyListQuery";
import { useCompanyBankDetailListQuery } from "Company/Modules/BankDetail/Api/Query/useCompanyBankDetailListQuery";
import {
	CountryEnum,
	CurrencyEnum,
	type MarketResponseDto,
} from "Infrastructure/Api/Api";
import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { useHandleServerError } from "Infrastructure/Utilities/Forms/Hooks/useHandleServerError";
import { createSelectOptions } from "Infrastructure/Utilities/Helpers/createSelectOptions";
import { useUpdateMarketMutation } from "Market/Api/Mutation/updateMarketMutation";
import {
	getMarketEditFromSchema,
	type MarketEditFormSchema,
} from "Market/Components/Form/Schema/marketEditFormSchema";
import { useTimezones } from "Market/Hooks/useTimezones";
import { OrderFieldOptionsModal } from "Settings/Modules/OrderSetup/Components/OrderFieldOptionsModal";
import FormSwitch from "Shared/Components/Form/CustomFormComponent/FormSwitch";
import Input from "Shared/Components/Form/CustomFormComponent/Input";
import Select from "Shared/Components/Form/CustomFormComponent/Select";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Grid2, Stack, Typography } from "@mui/material";
import { type FC, useEffect } from "react";
import { useForm } from "react-hook-form";

type Props = {
	market: MarketResponseDto;
};

const marketFormResource = Resources.base.market.forms;

export const MarketEditForm: FC<Props> = ({ market }) => {
	const { t } = useResource();
	const form = useForm<MarketEditFormSchema>({
		resolver: zodResolver(getMarketEditFromSchema(t)),
		defaultValues: market,
	});

	const {
		watch,
		control,
		formState: { errors },
		setValue,
	} = form;

	const { mutate: updateMarket, isPending } = useUpdateMarketMutation(
		market.id,
	);

	const { handleServerError } = useHandleServerError(form);

	const handleSubmitUpdate = () => {
		handleServerError(updateMarket);
	};

	const selectedCompanyId = watch("companyId");
	const { data: companyList } = useCompanyListQuery();

	const {
		data: companyBankDetailList,
		refetch: refetchCompanyBankDetails,
		isLoading: isLoadingCompanyBankDetails,
	} = useCompanyBankDetailListQuery(selectedCompanyId!);

	useEffect(() => {
		setValue("companyBankDetailId", undefined);
		selectedCompanyId && refetchCompanyBankDetails();
	}, [selectedCompanyId]);

	useEffect(() => {
		form.reset(market);
	}, [market.id]);

	const timezoneOptions = useTimezones();
	return (
		<form style={{ height: "100%" }}>
			<Stack height="100%" justifyContent="space-between">
				<Typography variant="h4">{t(marketFormResource.editMarket)}</Typography>
				<Grid2 container columnSpacing={2} mb={2}>
					<Grid2 size={{ xs: 12, md: 6 }}>
						<Input
							id="name"
							name="name"
							label={t(marketFormResource.labels.name)}
							variant="outlined"
							control={control}
							errors={errors}
							fullWidth
						/>
					</Grid2>
					<Grid2 size={{ xs: 12, md: 6 }}>
						<Select
							id="country"
							name="country"
							label={t(marketFormResource.labels.country)}
							variant="outlined"
							control={control}
							errors={errors}
							options={createSelectOptions(CountryEnum)}
							fullWidth
						/>
					</Grid2>
					<Grid2 size={{ xs: 12, md: 6 }}>
						<Select
							id="currency"
							name="currency"
							label={t(marketFormResource.labels.currency)}
							variant="outlined"
							control={control}
							errors={errors}
							options={createSelectOptions(CurrencyEnum)}
							fullWidth
						/>
					</Grid2>
					<Grid2 size={{ xs: 12, md: 6 }}>
						<Select
							id="timezone"
							name="timezone"
							label={t(marketFormResource.labels.timezone)}
							variant="outlined"
							control={control}
							errors={errors}
							options={timezoneOptions}
							fullWidth
						/>
					</Grid2>
					<Grid2 size={{ xs: 12, md: 6 }}>
						<Input
							id="web-referrer"
							name="webReferrer"
							label={t(marketFormResource.labels.webReferrer)}
							variant="outlined"
							control={control}
							errors={errors}
							fullWidth
						/>
					</Grid2>
					<Grid2 size={{ xs: 12 }} mt={2}>
						<Stack direction="row" spacing={2} alignItems="center">
							<OrderFieldOptionsModal
								title="Manage B2B partners"
								values={watch("b2bOptions")!}
								onAccept={(val) => {
									setValue("b2bOptions", val);
									handleSubmitUpdate();
								}}
							/>
						</Stack>
					</Grid2>
					<Grid2 size={{ xs: 12, md: 6 }}>
						<Select
							id="companyId"
							name="companyId"
							label={t(marketFormResource.labels.company)}
							variant="outlined"
							control={control}
							errors={errors}
							options={
								companyList?.map((m) => {
									return {
										label: m.name,
										value: m.id,
									};
								}) ?? []
							}
							fullWidth
						/>
					</Grid2>
					<Grid2 size={{ xs: 12, md: 6 }}>
						<Select
							id="companyBankDetailId"
							name="companyBankDetailId"
							label={t(marketFormResource.labels.companyBankDetail)}
							variant="outlined"
							control={control}
							errors={errors}
							options={
								companyBankDetailList?.map((m) => {
									return {
										label: m.name,
										value: m.id,
									};
								}) ?? []
							}
							disabled={!selectedCompanyId || isLoadingCompanyBankDetails}
							isLoading={isLoadingCompanyBankDetails}
							numeric
							fullWidth
						/>
					</Grid2>
				</Grid2>
				<Stack direction="row" alignItems="center" mb={2}>
					<FormSwitch control={control} name="isActive" label="Active" />
				</Stack>
				<Button
					variant="contained"
					onClick={handleSubmitUpdate}
					loading={isPending}
				>
					Save
				</Button>
			</Stack>
		</form>
	);
};
